import useMyBhStore from "@app/store/useMyBhStore";
import { TopicsData } from "@components/Blocks/FollowTrendingTopics/FollowTrendingTopics";
import { MYBH_TRENDING_TOPICS_PAGE_SIZE } from "@config/googleTrendingTopic";
import FollowTrendingTopicToastMsg from "@elements/ToastMessage/FollowTrendingTopicToastMsg";
import { GetUserInfo } from "@helper/baasHelper";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import useChartBeatRecommendedArticles from "@hooks/useChartBeatRecommendedArticles";
import useElementHeight from "@hooks/useElementHeight";
import { useFollowTrendingTags } from "@hooks/useFollowTrendingTags";
import useMySPHLightbox, { User } from "@hooks/useMySPHLightbox";
import { Route } from "@route-types/MyBH/+types/MyBHPage";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import useRecommendedArticles from "@src/app/hooks/useRecommendedArticles";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import { twMerge } from "tailwind-merge";

import MyBHMenu from "./Components/MyBHMenu";
import MyBHPageHeading from "./Components/MyBHPageHeading";
import RenderFollowing from "./Components/RenderFollowing";
import RenderRecommended from "./Components/RenderRecommended";
import useMyBHTab, { MyBHTab } from "./hooks/useMyBHTab";
import useShowStickyMenu from "./hooks/useShowStickyMenu";
import fetchMyBHData, { MyBHDataPayload } from "./MyBH.server";
import MyBHMetaData from "./MyBHMetaData";

type MyBHLoaderPayload = {
  initialTab: MyBHTab;
  data: MyBHDataPayload;
};

export function meta() {
  return [getMetaData(MyBHMetaData().metaTags)];
}

export default function MyBHPage() {
  const data = useLoaderData<TRouteWithRedirect<MyBHLoaderPayload, string>>();

  const serverArticles =
    data.type === ResponseType.SUCCESS ? data.payload.data.recentArticles : [];

  const { activeTab, setTab } = useMyBHTab(
    data.type === ResponseType.SUCCESS ? data.payload.initialTab : "untukmu",
  );
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { showStickyMenu } = useShowStickyMenu();

  const sectionName = "mybh";
  const adsSectionName = sectionName;
  const pageAdTarget = PageAdTargetingTypeEnum.LISTING;
  const mybhPageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    pageAdTargetValue: pageAdTarget,
  };

  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const virtualPVEvent = useBHVirtualPVAnalyticsCall();
  const { lightbox } = useMySPHLightbox();
  const currentUser = GetUserInfo();
  const visitorId = currentUser.visitorId;
  const isLoggedIn = !!visitorId;
  const userInfo = currentUser.userInfo as User;

  // ChartBeat recommendations now called directly from front-end
  // Device type and geographic location inferred from browser/IP
  const { articles } = useChartBeatRecommendedArticles({
    enabled: activeTab === "untukmu",
    initialArticles: serverArticles,
  });

  const startingLayoutArticles = articles.slice(0, 5);
  const layoutOneArticles = articles.slice(5, 10);
  const layoutTwoArticles = articles.slice(10);

  const trendingTopics = useMemo<TopicsData[]>(
    () =>
      data.type === ResponseType.SUCCESS
        ? data.payload.data.trendingTopics
        : [],
    [data],
  );

  // ================= TAB CHANGE EVENT =================
  const handleTabChange = useCallback(
    (tab: MyBHTab) => {
      setTab(tab);

      requestAnimationFrame(() => {
        contentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    [setTab],
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // skip the initial render, pageview already fired in loader
    }

    virtualPVEvent(activeTab);
  }, [activeTab, virtualPVEvent]);

  const followedTopics = useMyBhStore((state) => state.tags);

  const followedIds = useMemo(
    () => followedTopics.map((t) => t.cueId),
    [followedTopics],
  );

  const userKeywords = useMemo(
    () => followedTopics.map((t) => t.name.toLowerCase()).join("|"),
    [followedTopics],
  );

  const {
    topics,
    toggleTopic,
    shuffleTopics,
    isSelected,
    isFollowing,
    loadingTopicId,
    showToast,
    toastCount,
  } = useFollowTrendingTags({
    topics: trendingTopics,
    visitorId,
    isLoggedIn,
    followedIds,
    userKeywords,
    fetchMoreTopics: () => Promise.resolve(trendingTopics),
    pageSize: MYBH_TRENDING_TOPICS_PAGE_SIZE,
  });

  const selectedIds = useMemo(
    () => topics.filter((t) => isSelected(t.topicId)).map((t) => t.topicId),
    [topics, isSelected],
  );

  const newlyFollowedIds = useMemo(() => {
    if (toastCount === 0) return [];
    return topics.filter((t) => isFollowing(t.topicId)).map((t) => t.topicId);
  }, [topics, isFollowing, toastCount]);

  const tagNames = useMemo(
    () => followedTopics.map((t) => t.name.toLowerCase().replace(/\s+/g, "-")),
    [followedTopics],
  );

  const {
    articles: followedArticles,
    loadMore: loadMoreFollowedArticles,
    hasMore: hasMoreFollowedArticles,
    pagination: followedArticlesPagination,
  } = useRecommendedArticles({
    tags: tagNames,
    size: 20,
    enabled: isLoggedIn && activeTab === "pilihanku" && followedIds.length > 0,
  });

  const headerHeight = useElementHeight("header-container");

  return (
    <BHBasePage {...mybhPageProps}>
      <FollowTrendingTopicToastMsg
        followedCount={toastCount}
        forceShowToast={showToast}
      />
      <MyBHPageHeading
        visitorId={visitorId}
        userInfo={userInfo}
        hideMenu={showStickyMenu}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <div
        style={{
          top: `${headerHeight}px`,
        }}
        className={twMerge(
          "sticky z-50 transition-all duration-300",
          showStickyMenu
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none absolute",
        )}
      >
        <MyBHMenu
          isSticky
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      <div
        ref={contentRef}
        className="w-full scroll-mt-[280px] lg:scroll-mt-[320px]"
      >
        {/* untukmu content */}
        <RenderRecommended
          activeTab={activeTab}
          startingLayoutArticles={startingLayoutArticles}
          layoutOneArticles={layoutOneArticles}
          layoutTwoArticles={layoutTwoArticles}
          trendingTopics={topics}
          adsSectionName={adsSectionName}
          pageAdTarget={pageAdTarget}
          isLoggedIn={isLoggedIn}
          toggleTopic={toggleTopic}
          shuffleTopics={shuffleTopics}
          followedIds={newlyFollowedIds}
          selectedIds={selectedIds}
          lightbox={lightbox}
          loadingTopicId={loadingTopicId}
          bhCustomEvent={bhCustomEvent}
          userKeywords={userKeywords}
        />

        {/* pilihanku content */}
        <RenderFollowing
          activeTab={activeTab}
          trendingTopics={topics}
          followedArticles={followedArticles}
          onLoadMoreFollowedArticles={loadMoreFollowedArticles}
          hasMoreFollowedArticles={hasMoreFollowedArticles}
          followedArticlesPage={followedArticlesPagination?.current ?? 0}
          adsSectionName={adsSectionName}
          pageAdTarget={pageAdTarget}
          isLoggedIn={isLoggedIn}
          toggleTopic={toggleTopic}
          shuffleTopics={shuffleTopics}
          followedIds={newlyFollowedIds}
          selectedIds={selectedIds}
          lightbox={lightbox}
          loadingTopicId={loadingTopicId}
          bhCustomEvent={bhCustomEvent}
          userKeywords={userKeywords}
        />
      </div>
    </BHBasePage>
  );
}

export async function loader({
  request,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<MyBHLoaderPayload | null, string>
> {
  const { gaData } = MyBHMetaData();
  const url = new URL(request.url, "http://localhost");
  const tab = url.searchParams.get("tab");
  const initialData = await fetchMyBHData();

  return Promise.resolve({
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: {
      initialTab: tab === "pilihanku" ? "pilihanku" : "untukmu",
      data: initialData,
    },
    gtm: getGTMData(gaData),
  });
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const url = new URL(args.request.url);
  const tab = url.searchParams.get("tab");

  const result = (await fetchContext(args)) as TRouteWithoutRedirect<
    MyBHDataPayload,
    string
  > | null;

  if (!result || result.type !== ResponseType.SUCCESS) {
    console.error(
      "Error occurred during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  return {
    ...result,
    payload: {
      initialTab: tab === "pilihanku" ? "pilihanku" : "untukmu",
      data: result.payload,
    },
  };
}
